import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { renderTextArea } from '../../../shared/formComponents';
import Tooltip from '../../../../../shared/components/widgets/Tooltip';
import CollapsibleSection from './CollapsibleSection';
import GoogleConstantMultiSelect from './GoogleConstantMultiSelect';
import {
  searchGoogleGeoConstants,
  getGoogleGeoConstantsByResourceNames,
  searchMetaCountries,
  getMetaCountriesByResourceNames,
  searchIsoCountries,
  getIsoCountriesByResourceNames,
} from '../../../shared/budgetApi';

/**
 * Reusable geo-targeting component for Google/Meta/TikTok platforms
 * TikTok/Meta: Countries always visible (all modes/views)
 * Google: Countries only in Limitless View, states/cities/postal in Advanced+Limitless
 */
const GeoTargetingFields = ({
  setupFlow,
  agencyInternal,
  platform = 'google',
  supportsCities = true,
  supportsStates = true,
  supportsPostalCodes = true,
  accountId,
}) => {
  const isAdvancedLimitless = setupFlow?.value === 'advanced' && agencyInternal?.value;
  const isGooglePlatform = platform === 'google';
  const isMetaPlatform = platform === 'meta';
  const isTikTokPlatform = platform === 'tiktok';

  // TikTok/Meta: always show countries. Google: only in Limitless View
  const showCountries = platform === 'tiktok' || platform === 'meta' || agencyInternal?.value;

  const renderCountriesField = () => {
    if (isGooglePlatform) {
      return (
        <Field
          key="geoCountries"
          name="geoCountries"
          component={GoogleConstantMultiSelect}
          label="Countries (Optional)"
          tooltip="Search and select the countries to target for your Google ads"
          placeholder="Search countries (e.g., United States, Canada)..."
          searchFn={searchGoogleGeoConstants}
          lookupByResourceNamesFn={getGoogleGeoConstantsByResourceNames}
          extraSearchParams={{ uiCategory: 'COUNTRY' }}
        />
      );
    }

    if (isMetaPlatform) {
      return (
        <Field
          key="geoCountries"
          name="geoCountries"
          component={GoogleConstantMultiSelect}
          label="Countries"
          tooltip="Search and select the countries to target for your Meta ads"
          placeholder="Search countries (e.g., United States, Canada)..."
          searchFn={searchMetaCountries}
          lookupByResourceNamesFn={getMetaCountriesByResourceNames}
          extraSearchParams={{ accountId }}
        />
      );
    }

    if (isTikTokPlatform) {
      return (
        <Field
          key="geoCountries"
          name="geoCountries"
          component={GoogleConstantMultiSelect}
          label="Countries"
          tooltip="Search and select the countries to target for your TikTok ads"
          placeholder="Search countries (e.g., United States, Canada)..."
          searchFn={searchIsoCountries}
          lookupByResourceNamesFn={getIsoCountriesByResourceNames}
        />
      );
    }

    return (
      <div className="form__form-group">
        <span className="form__form-group-label">
          Countries{' '}
          <Tooltip
            id="geoCountries"
            msg="Enter comma-separated 2-character country codes (e.g., US, CA, MX)"
            placement="top"
          />
        </span>
        <div className="form__form-group-field">
          <Field
            name="geoCountries"
            component={renderTextArea}
            placeholder="US, CA, MX"
          />
        </div>
      </div>
    );
  };

  const countriesField = showCountries && renderCountriesField();

  const statesField = isAdvancedLimitless && supportsStates && (
    isGooglePlatform ? (
      <Field
        key="geoStates"
        name="geoStates"
        component={GoogleConstantMultiSelect}
        label="States (Optional)"
        tooltip="Search and select the states or regions to target for your Google ads"
        placeholder="Search states (e.g., California, Ontario)..."
        searchFn={searchGoogleGeoConstants}
        lookupByResourceNamesFn={getGoogleGeoConstantsByResourceNames}
        extraSearchParams={{ uiCategory: 'STATE' }}
      />
    ) : (
      <div className="form__form-group">
        <span className="form__form-group-label">
          States (Optional) <Tooltip id="geoStates" msg="Enter comma-separated state or region codes" placement="top" />
        </span>
        <div className="form__form-group-field">
          <Field
            name="geoStates"
            component={renderTextArea}
            placeholder="State or region code"
          />
        </div>
      </div>
    )
  );

  const citiesField = isAdvancedLimitless && supportsCities && (
    isGooglePlatform ? (
      <Field
        key="geoCities"
        name="geoCities"
        component={GoogleConstantMultiSelect}
        label="Cities (Optional)"
        tooltip="Search and select the cities to target for your Google ads"
        placeholder="Type at least 3 characters to search cities..."
        searchFn={searchGoogleGeoConstants}
        lookupByResourceNamesFn={getGoogleGeoConstantsByResourceNames}
        extraSearchParams={{ uiCategory: 'CITY' }}
        minSearchLength={3}
      />
    ) : (
      <div className="form__form-group">
        <span className="form__form-group-label">
          Cities (Optional) <Tooltip id="geoCities" msg="Enter comma-separated city names" placement="top" />
        </span>
        <div className="form__form-group-field">
          <Field
            name="geoCities"
            component={renderTextArea}
            placeholder="City name"
          />
        </div>
      </div>
    )
  );

  const postalCodesField = isAdvancedLimitless && supportsPostalCodes && (
    isGooglePlatform ? (
      <Field
        key="geoPostalCodes"
        name="geoPostalCodes"
        component={GoogleConstantMultiSelect}
        label="Postal Codes (Optional)"
        tooltip="Search and select the postal codes to target for your Google ads"
        placeholder="Type at least 3 characters to search postal codes..."
        searchFn={searchGoogleGeoConstants}
        lookupByResourceNamesFn={getGoogleGeoConstantsByResourceNames}
        extraSearchParams={{ uiCategory: 'POSTAL_CODE' }}
        minSearchLength={3}
      />
    ) : (
      <div className="form__form-group">
        <span className="form__form-group-label">
          Postal Codes (Optional) <Tooltip id="geoPostalCodes" msg="Enter comma-separated postal codes" placement="top" />
        </span>
        <div className="form__form-group-field">
          <Field
            name="geoPostalCodes"
            component={renderTextArea}
            placeholder="Postal code"
          />
        </div>
      </div>
    )
  );

  // Google: group all optional geo fields into a collapsible "Geo Settings (Optional)" section
  if (isGooglePlatform) {
    return (
      <CollapsibleSection title="Geo Settings (Optional)" defaultOpen={false}>
        {countriesField}
        {statesField}
        {citiesField}
        {postalCodesField}
      </CollapsibleSection>
    );
  }

  // TikTok/Meta: keep fields ungrouped as before
  return (
    <>
      {countriesField}
      {statesField}
      {citiesField}
      {postalCodesField}
    </>
  );
};

GeoTargetingFields.propTypes = {
  setupFlow: PropTypes.shape({
    value: PropTypes.string,
  }),
  agencyInternal: PropTypes.shape({
    value: PropTypes.bool,
  }),
  platform: PropTypes.oneOf(['google', 'meta', 'tiktok']),
  supportsCities: PropTypes.bool,
  supportsStates: PropTypes.bool,
  supportsPostalCodes: PropTypes.bool,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

GeoTargetingFields.defaultProps = {
  setupFlow: null,
  agencyInternal: null,
  platform: 'google',
  supportsCities: true,
  supportsStates: true,
  supportsPostalCodes: true,
  accountId: null,
};

export default GeoTargetingFields;
